import type {
  AgaveCostCode,
  AgaveCostType,
  AgaveGlCode,
  AgaveProject,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubaccount,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  CreateAgaveCostCodeInput,
  CreateAgaveCostTypeInput,
  CreateAgaveGlCodeInput,
  CreateAgaveProjectInput,
  CreateAgaveSubJobInput,
  CreateAgaveVendorInput,
  CreateAgaveLocationInput,
  CreateAgaveDepartmentInput,
  CreateAgaveSubaccountInput,
  CreateAgaveEmployeeEntityInput,
  FlexCode,
  FlexCostCode,
  FlexCostType,
  FlexGlCode,
  FlexVendor,
  HighnoteTransactionEvent,
} from 'API';
import { ReturnedStatus, WorkflowStatus } from 'API';
import { captureException } from '@sentry/react';
import { isReturned } from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { isTransactionFinalized } from 'app/services/agave/erpUtils';
import { yupResolver } from '@hookform/resolvers/yup';
import { fileAccessService } from 'app/services/fileAccess.service';
import { CreateReceiptInput } from 'app/shared-components/receipts/CreateReceiptInput';
import { updateTransactionAgaveClient } from 'app/pages/store/agaveSlice';
import { type ErpPostingAccount } from 'app/services/erp-connection.service';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { removeTypename } from 'util/helpers';
import { createReceiptUpload } from 'app/services/reimbursementService';
import { isNetworkTransportError } from 'app/services/networkTransport';
import { downscaleReceiptImage } from 'app/util/receiptImage';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { CURRENT_VALUE_KEYS } from 'app/pages/mobile/types/mobileCodingTypes';
import { getSubmitBlockers, isSubmitBlocked, type SubmitBlockers } from 'util/submitRequirements';
import { changedCodingFields } from './mobileCodingChanges';
import { useCallback, useContext, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { MAX_RECEIPT_SIZE, validMimeTypes } from 'app/shared-components/receipts/ReceiptConstants';
import {
  applyLocalTransactionUpdates,
  markTransactionResubmitted,
  updateTransaction,
  updateTransactionFlexCode,
  updateTransactionFlexCostCode,
  updateTransactionFlexCostType,
  updateTransactionFlexGlCode,
  updateTransactionFlexVendor,
  updateTransactionNote,
  updateTransactionReceipt,
} from 'app/pages/store/transactionsSlice';
import { AgaveEmployeeClearedContext } from 'app/pages/overview/tabs/transactions/AgaveTransactions/utils';

/** SEE-1842: the server's own reason when it refused a submit as incomplete, which names what is
 *  missing; anything else stays the generic message. */
const submitRefusal = (error: unknown): string | null => {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.startsWith('Missing before submitting:') ? message : null;
};

// Form Schema
const schema = yup.object().shape({
  receiptFile: yup
    .mixed<File>()
    .nullable()
    .test('fileType', 'Invalid file type', (value) => {
      if (!value) return true;
      return validMimeTypes.includes(value.type);
    })
    .test('fileSize', 'File is too large', (value) => {
      if (!value) return true;
      return value.size <= MAX_RECEIPT_SIZE;
    }),
  note: yup.string().nullable(),
  glCode: yup.mixed().nullable(),
  projectCode: yup.mixed().nullable(),
  costCode: yup.mixed().nullable(),
  costType: yup.mixed().nullable(),
  subJob: yup.mixed().nullable(),
  vendor: yup.mixed().nullable(),
  location: yup.mixed().nullable(),
  department: yup.mixed().nullable(),
  subaccount: yup.mixed().nullable(),
  employee: yup.mixed().nullable(),
  billableItem: yup.mixed().nullable(),
  equipment: yup.mixed().nullable(),
  emCostCode: yup.mixed().nullable(),
  emCostType: yup.mixed().nullable(),
  codingType: yup.string().nullable(),
  // Fuel capture fields
  fuelVehicleId: yup.string().nullable(),
  fuelGallons: yup.number().nullable(),
  fuelOdometerReading: yup.number().nullable(),
  fuelCostPerGallon: yup.number().nullable(),
});

export interface TransactionFormValues {
  receiptFile?: File;
  note: string | null;
  glCode: (FlexGlCode | AgaveGlCode) | null;
  projectCode: (FlexCode | AgaveProject) | null;
  costCode: (FlexCostCode | AgaveCostCode) | null;
  costType: (FlexCostType | AgaveCostType) | null;
  subJob: AgaveSubJob | null;
  vendor: (FlexVendor | AgaveVendor) | null;
  location: AgaveLocation | null;
  department: AgaveDepartment | null;
  subaccount: AgaveSubaccount | null;
  employee: AgaveEmployeeEntity | null;
  billableItem: ErpPostingAccount | null;
  equipment: AgaveEquipment | null;
  emCostCode: AgaveEmCostCode | null;
  emCostType: AgaveEmCostType | null;
  codingType: string | null;
  // Fuel capture fields
  fuelVehicleId: string | null;
  fuelGallons: number | null;
  fuelOdometerReading: number | null;
  fuelCostPerGallon: number | null;
}

interface TransactionFormProps {
  transaction: HighnoteTransactionEvent | undefined;
  paidolId: string;
  /** Whether the transaction already has a receipt linked, which hasReceipt catches up with later. */
  hasLinkedReceipts: boolean;
  onClose: () => void;
}

/** The coding keyed by column, which is how the submit rule reads it. */
const codingByColumn = (values: TransactionFormValues) =>
  Object.fromEntries(Object.entries(CURRENT_VALUE_KEYS).map(([column, key]) => [column, key && values[key]]));

// Reusable close button component for persistent snackbars
const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    style={{
      color: 'white',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
    }}
  >
    ✕
  </button>
);

export const useTransactionForm = ({
  transaction,
  paidolId,
  hasLinkedReceipts,
  onClose,
}: TransactionFormProps) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const registerEmployeeCleared = useContext(AgaveEmployeeClearedContext);
  const { codingSystemType } = useCodingSystemConfig();
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);
  // Receipt progress from a save attempt that then failed — a retry of the same
  // file reuses the uploaded key (and the created ReceiptUpload) instead of
  // duplicating them (SEE-1395). Cleared only when the whole save succeeds.
  const uploadedReceiptRef = useRef<{ file: File; key: string; size?: number; receiptId?: string } | null>(
    null
  );

  const form = useForm<TransactionFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      receiptFile: undefined,
      note: transaction?.note ?? '',
      glCode: transaction?.flexGlCode || transaction?.agaveGlCode || null,
      projectCode: transaction?.flexCode || transaction?.agaveProject || null,
      costCode: transaction?.flexCostCode || transaction?.agaveCostCode || null,
      costType: transaction?.flexCostType || transaction?.agaveCostType || null,
      subJob: transaction?.agaveSubJob || null,
      vendor: transaction?.flexVendor || transaction?.agaveVendor || null,
      location: transaction?.agaveLocation || null,
      department: transaction?.agaveDepartment || null,
      subaccount: transaction?.agaveSubaccount || null,
      employee: transaction?.agaveEmployee || null,
      // SEE-1135 — reconstruct the ErpPostingAccount from the flat row fields.
      billableItem: transaction?.erpBillableItemId
        ? {
            accountId: transaction.erpBillableItemId,
            accountName: transaction.erpBillableItemName ?? transaction.erpBillableItemId,
          }
        : null,
      equipment: transaction?.agaveEquipment || null,
      emCostCode: transaction?.agaveEmCostCode || null,
      emCostType: transaction?.agaveEmCostType || null,
      codingType: transaction?.codingType ?? null,
      // Fuel capture fields
      fuelVehicleId: transaction?.fuelVehicleId ?? null,
      fuelGallons: transaction?.fuelGallons ?? null,
      fuelOdometerReading: transaction?.fuelOdometerReading ?? null,
      fuelCostPerGallon: transaction?.fuelCostPerGallon ?? null,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  // Helper function to build coding field updates
  const buildCodingFieldUpdates = useCallback(
    (data: TransactionFormValues, transactionId: string) => {
      const updatePromises: any[] = [];

      // VALIDATION: Check subjob and cost code compatibility
      const modifiedData = { ...data };

      // Validate subjob belongs to selected project
      if (modifiedData.subJob && modifiedData.projectCode) {
        const subJobAsAgave = modifiedData.subJob as AgaveSubJob;
        const projectAsAgave = modifiedData.projectCode as AgaveProject;

        // Only invalidate if SubJob has a specific projectId that doesn't match
        // SubJobs with null projectId are not restricted to a specific project
        // (they're linked via AgaveProjectSubJob join table, not directly)
        if (subJobAsAgave.projectId && subJobAsAgave.projectId !== projectAsAgave.id) {
          const subJobName = subJobAsAgave.name || subJobAsAgave.number || 'selected sub-job';
          const projectName = projectAsAgave.name || projectAsAgave.number || 'selected project';

          enqueueSnackbar(
            t('transactionSidesheet.subJobIncompatibleWithProject', {
              subJob: subJobName,
              project: projectName,
              defaultValue: `${subJobName} cannot be used with ${projectName}`,
            }),
            {
              variant: 'warning',
              persist: true,
              action: (key) => <CloseButton onClick={() => closeSnackbar(key)} />,
            }
          );
          // Clear the subjob
          modifiedData.subJob = null;
        }
      }

      // Validate cost code belongs to selected subjob
      if (modifiedData.subJob && modifiedData.costCode) {
        const subJobAsAgave = modifiedData.subJob as AgaveSubJob;
        const costCodeAsAgave = modifiedData.costCode as AgaveCostCode;
        const subjobCostCodeIds = subJobAsAgave.cost_code_ids || [];

        if (subjobCostCodeIds.length > 0 && !subjobCostCodeIds.includes(costCodeAsAgave.id)) {
          const costCodeName = costCodeAsAgave.name || costCodeAsAgave.code || 'selected cost code';
          const subJobName = subJobAsAgave.name || subJobAsAgave.number || 'selected sub-job';

          enqueueSnackbar(
            t('transactionSidesheet.costCodeSubJobIncompatible', {
              costCode: costCodeName,
              subJob: subJobName,
              defaultValue: `${costCodeName} cannot be used with ${subJobName}`,
            }),
            {
              variant: 'warning',
              persist: true,
              action: (key) => <CloseButton onClick={() => closeSnackbar(key)} />,
            }
          );
          // Clear the cost code
          modifiedData.costCode = null;
        }
      }

      // SEE-1899: send only what differs from what the sheet opened with. It runs after the checks
      // above, so a dependent they cleared is sent. Sending every field wrote back coding the user
      // never touched, and stamped the row as coded by hand.
      const changed = changedCodingFields(form.formState.defaultValues, modifiedData);

      // Route by paidol coding system, not by value shape — clearing all codes leaves
      // every field null, and a null has no shape, which would misroute the unset
      // through the Flex branch and never clear the Agave fields. (SEE-807)
      // Only Agave needs the batched-input thunk; every non-Agave path that reaches
      // this form (Flex; jobCode paidols don't render coding fields here) goes through
      // the per-field Flex thunks below.
      if (codingSystemType === 'agave') {
        // Use Agave client update for Agave data
        const agaveUpdates: any = {
          id: transactionId,
        };

        if (changed.has('glCode')) {
          agaveUpdates.agaveGlCode = modifiedData.glCode
            ? (removeTypename(modifiedData.glCode) as CreateAgaveGlCodeInput)
            : null;
        }
        if (changed.has('projectCode')) {
          agaveUpdates.agaveProject = modifiedData.projectCode
            ? (removeTypename(modifiedData.projectCode) as CreateAgaveProjectInput)
            : null;
        }
        if (changed.has('costCode')) {
          agaveUpdates.agaveCostCode = modifiedData.costCode
            ? (removeTypename(modifiedData.costCode) as CreateAgaveCostCodeInput)
            : null;
        }
        if (changed.has('costType')) {
          agaveUpdates.agaveCostType = modifiedData.costType
            ? (removeTypename(modifiedData.costType) as CreateAgaveCostTypeInput)
            : null;
        }
        if (changed.has('subJob')) {
          agaveUpdates.agaveSubJob = modifiedData.subJob
            ? (removeTypename(modifiedData.subJob) as CreateAgaveSubJobInput)
            : null;
        }
        if (changed.has('vendor')) {
          agaveUpdates.agaveVendor = modifiedData.vendor
            ? (removeTypename(modifiedData.vendor) as CreateAgaveVendorInput)
            : null;
        }
        if (changed.has('location')) {
          agaveUpdates.agaveLocation = modifiedData.location
            ? (removeTypename(modifiedData.location) as CreateAgaveLocationInput)
            : null;
        }
        if (changed.has('department')) {
          agaveUpdates.agaveDepartment = modifiedData.department
            ? (removeTypename(modifiedData.department) as CreateAgaveDepartmentInput)
            : null;
        }
        if (changed.has('subaccount')) {
          agaveUpdates.agaveSubaccount = modifiedData.subaccount
            ? (removeTypename(modifiedData.subaccount) as CreateAgaveSubaccountInput)
            : null;
        }
        if (changed.has('employee')) {
          agaveUpdates.agaveEmployee = modifiedData.employee
            ? (removeTypename(modifiedData.employee) as CreateAgaveEmployeeEntityInput)
            : null;
        }
        // Outside the changed check: a null employee, sent or not, still stops the desktop
        // auto-fill putting one back.
        if (!modifiedData.employee) {
          registerEmployeeCleared?.(transactionId);
        }
        // SEE-1135 — flat id + denormalized name (not an entity object mapped to an
        // id like the agave* fields). updateTransactionAgaveClient forwards both.
        if (changed.has('billableItem')) {
          const item = modifiedData.billableItem as ErpPostingAccount | null;
          agaveUpdates.erpBillableItemId = item?.accountId ?? null;
          agaveUpdates.erpBillableItemName = item?.accountId ? item.accountName ?? item.accountId : null;
        }

        // SEE-1091 — equipment coding (Vista). Gated: only sent when the flag is
        // on, so flag-off writes are byte-for-byte unchanged. agaveUpdates is
        // typed `any`; the thunk reads `.id` off each object.
        if (isEquipmentCodingEnabled) {
          if (changed.has('equipment')) {
            agaveUpdates.agaveEquipment = modifiedData.equipment
              ? removeTypename(modifiedData.equipment)
              : null;
          }
          if (changed.has('emCostCode')) {
            agaveUpdates.agaveEmCostCode = modifiedData.emCostCode
              ? removeTypename(modifiedData.emCostCode)
              : null;
          }
          if (changed.has('emCostType')) {
            agaveUpdates.agaveEmCostType = modifiedData.emCostType
              ? removeTypename(modifiedData.emCostType)
              : null;
          }
          // Send codingType when it changed, null when deselected, so reverting to untyped
          // persists.
          if (changed.has('codingType')) {
            agaveUpdates.codingType = modifiedData.codingType ? modifiedData.codingType.toUpperCase() : null;
          }
        }

        // Nothing to write when no coding changed.
        if (Object.keys(agaveUpdates).length > 1) {
          updatePromises.push(dispatch(updateTransactionAgaveClient(agaveUpdates)));
        }
      } else {
        // Use Flex-specific updates for Flex data
        if (changed.has('glCode')) {
          updatePromises.push(
            dispatch(
              updateTransactionFlexGlCode({
                paidolId,
                id: transactionId,
                flexGlCodeId: modifiedData.glCode?.id ?? null,
              })
            )
          );
        }

        if (changed.has('projectCode')) {
          updatePromises.push(
            dispatch(
              updateTransactionFlexCode({
                paidolId,
                id: transactionId,
                flexCodeId: modifiedData.projectCode?.id ?? null,
              })
            )
          );
        }

        if (changed.has('costCode')) {
          updatePromises.push(
            dispatch(
              updateTransactionFlexCostCode({
                paidolId,
                id: transactionId,
                flexCostCodeId: modifiedData.costCode?.id ?? null,
              })
            )
          );
        }

        if (changed.has('costType')) {
          updatePromises.push(
            dispatch(
              updateTransactionFlexCostType({
                paidolId,
                id: transactionId,
                flexCostTypeId: modifiedData.costType?.id ?? null,
              })
            )
          );
        }

        if (changed.has('vendor')) {
          updatePromises.push(
            dispatch(
              updateTransactionFlexVendor({
                paidolId,
                id: transactionId,
                flexVendorId: modifiedData.vendor?.id ?? null,
              })
            )
          );
        }
      }

      // `saved` is the coding as written, after the compatibility checks above cleared what didn't fit.
      return { updatePromises, saved: modifiedData };
    },
    [
      dispatch,
      form,
      paidolId,
      enqueueSnackbar,
      closeSnackbar,
      t,
      registerEmployeeCleared,
      codingSystemType,
      isEquipmentCodingEnabled,
    ]
  );

  // Helper function to build note and approval updates
  const buildMetadataUpdates = useCallback(
    (data: TransactionFormValues, transactionId: string) => {
      const updatePromises: any[] = [];

      if (data.note !== transaction?.note) {
        updatePromises.push(
          dispatch(
            updateTransactionNote({
              paidolId,
              id: transactionId,
              note: data.note || '',
            })
          )
        );
      }

      return updatePromises;
    },
    [dispatch, paidolId, transaction]
  );

  // Resolves to what held the submit back (SEE-1842), or null when nothing did.
  const onSubmit = useCallback(
    async (data: TransactionFormValues): Promise<SubmitBlockers | null> => {
      if (!transaction?.id) {
        return null;
      }

      const mimeToExtension: Record<string, string> = {
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'application/pdf': 'pdf',
      };

      try {
        const updatePromises: any[] = [];

        // Handle receipt upload if present. The receipt link runs sequentially,
        // NOT inside the Promise.all below: on a flaky connection the S3 upload
        // can succeed and the link calls die in transit, and bundling them meant
        // one rejection discarded everything including the already-uploaded file
        // (SEE-1395 — orphaned receipts). The sheet stays open on failure, so the
        // user can retry; uploadedReceiptRef lets that retry skip the re-upload.
        if (data.receiptFile) {
          let uploadedKey: string;
          if (uploadedReceiptRef.current?.file === data.receiptFile) {
            uploadedKey = uploadedReceiptRef.current.key;
          } else {
            // SEE-1424: shrink camera photos before they cross a weak connection.
            const upload = await downscaleReceiptImage(data.receiptFile);
            const fileExtension = mimeToExtension[upload.type] || 'jpg';
            const fileName = `receipt_${Date.now()}.${fileExtension}`;
            // SEE-1319: the key is chosen server-side under this company's
            // transactionReceipts/<transaction> folder.
            const uploadedFileResult = await fileAccessService.uploadFile({
              kind: 'TRANSACTION_RECEIPT',
              paidolId,
              resourceId: transaction.id,
              file: upload,
              fileName,
              contentType: upload.type,
            });
            uploadedReceiptRef.current = {
              file: data.receiptFile,
              key: uploadedFileResult.key,
              size: upload.size,
            };
            uploadedKey = uploadedFileResult.key;
          }

          // A retry after the link already succeeded must not create a second
          // ReceiptUpload row; the legacy-pointer update below is idempotent.
          let receiptId = uploadedReceiptRef.current?.receiptId;
          if (!receiptId) {
            // Pass highnoteTransactionEventId when creating receipt upload
            const receiptUploadInput = new CreateReceiptInput(
              uploadedKey,
              paidolId,
              transaction.id, // highnoteTransactionEventId
              undefined,
              undefined,
              uploadedReceiptRef.current?.size // SEE-1601
            );
            const receiptUpload = await createReceiptUpload(receiptUploadInput);
            receiptId = receiptUpload.id;
            uploadedReceiptRef.current = {
              ...uploadedReceiptRef.current,
              file: data.receiptFile,
              key: uploadedKey,
              receiptId,
            };
          }

          // unwrap() so a failed link rejects into the catch below instead of
          // resolving as a rejected thunk action.
          await dispatch(
            updateTransactionReceipt({
              paidolId,
              id: transaction.id,
              receiptPath: uploadedKey,
              receiptId,
            })
          ).unwrap();
        }

        // Add coding field updates
        const coding = buildCodingFieldUpdates(data, transaction.id);
        updatePromises.push(...coding.updatePromises);

        // Add note and approval updates
        updatePromises.push(...buildMetadataUpdates(data, transaction.id));

        // Add fuel capture updates if any values changed
        const fuelUpdates: {
          id: string;
          fuelVehicleId?: string | null;
          fuelGallons?: number | null;
          fuelOdometerReading?: number | null;
          fuelCostPerGallon?: number | null;
        } = { id: transaction.id };
        let hasFuelUpdates = false;

        if (data.fuelVehicleId !== (transaction.fuelVehicleId ?? null)) {
          fuelUpdates.fuelVehicleId = data.fuelVehicleId;
          hasFuelUpdates = true;
        }
        if (data.fuelGallons !== (transaction.fuelGallons ?? null)) {
          fuelUpdates.fuelGallons = data.fuelGallons;
          hasFuelUpdates = true;
        }
        if (data.fuelOdometerReading !== (transaction.fuelOdometerReading ?? null)) {
          fuelUpdates.fuelOdometerReading = data.fuelOdometerReading;
          hasFuelUpdates = true;
        }
        if (data.fuelCostPerGallon !== (transaction.fuelCostPerGallon ?? null)) {
          fuelUpdates.fuelCostPerGallon = data.fuelCostPerGallon;
          hasFuelUpdates = true;
        }

        if (hasFuelUpdates) {
          updatePromises.push(dispatch(updateTransaction(fuelUpdates)));
        }

        // unwrap() so a failed coding, note or fuel save rejects into the catch below
        // instead of going on to submit a transaction that is missing it.
        await Promise.all(updatePromises.map((update) => update.unwrap()));

        // Saving a returned transaction resubmits it, and saving a New one submits it.
        // SEE-1178: a finalized (posted/bypassed/approved) transaction must not be
        // resubmitted, even if it's still flagged RETURNED — skip the reset.
        const resubmits = isReturned(transaction.returnedStatus) && !isTransactionFinalized(transaction);
        const submits = transaction.workflowStatus === WorkflowStatus.NEW;
        // SEE-1842: the edits are saved either way, but neither goes ahead while the row is
        // missing what the company requires; the sheet then says what that is.
        const blockers = getSubmitBlockers(
          { ...transaction, note: data.note, codingType: data.codingType },
          selectedPaidol,
          {
            hasAttachedReceipt: hasLinkedReceipts || !!data.receiptFile,
            coding: codingByColumn(coding.saved),
          }
        );
        const heldBackBy = (resubmits || submits) && isSubmitBlocked(blockers) ? blockers : null;

        // Send RESUBMITTED last, after coding is persisted, so the server resets the review
        // status to needs-review and notifies subscribed admins against the updated record
        // (mirrors the web handleResubmit; admin notification is driven off the DDB stream).
        if (resubmits && !heldBackBy) {
          // unwrap() so a failed resubmit rejects into the catch below.
          await dispatch(
            updateTransaction({ id: transaction.id, returnedStatus: ReturnedStatus.RESUBMITTED })
          ).unwrap();
          // Drop it from the Actions list now and keep it suppressed across the
          // eventually-consistent OpenSearch refetch (it lags the DDB write, so the
          // refetch still returns it as RETURNED for a moment — SEE-1079 only covers
          // the receipt live-check, not returnedStatus).
          dispatch(markTransactionResubmitted(transaction.id));
        }

        // SEE-1291: saving a New transaction submits it into review
        // (New -> Needs Review), mirroring the returned -> resubmit flow above.
        if (submits && !heldBackBy) {
          await dispatch(
            updateTransaction({ id: transaction.id, workflowStatus: WorkflowStatus.NEEDS_REVIEW })
          ).unwrap();
          // The transactions list reads the entity adapter — optimistically flip the
          // row so it shows Needs Review immediately on reopen instead of reverting to
          // New until a fresh search catches up.
          dispatch(
            applyLocalTransactionUpdates([
              { id: transaction.id, changes: { workflowStatus: WorkflowStatus.NEEDS_REVIEW } },
            ])
          );
        }

        uploadedReceiptRef.current = null;
        onClose();
        return heldBackBy;
      } catch (error) {
        captureException(error);
        const message = isNetworkTransportError(error)
          ? t(
              'connectionDroppedRetry',
              'Connection dropped while saving. Your changes are still here — check your signal and tap Save to retry.'
            )
          : submitRefusal(error) ?? t('somethingWentWrong', 'Something went wrong. Please try again.');
        enqueueSnackbar(message, {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          style: { marginBottom: '80px' },
          action: (key) => <CloseButton onClick={() => closeSnackbar(key)} />,
        });
        // Rethrow so the edit sheet's handleSave skips the success toast.
        throw error;
      }
    },
    [
      transaction,
      paidolId,
      selectedPaidol,
      hasLinkedReceipts,
      dispatch,
      onClose,
      buildCodingFieldUpdates,
      buildMetadataUpdates,
      enqueueSnackbar,
      closeSnackbar,
      t,
    ]
  );

  // react-hook-form 7.44 leaves isSubmitting set when the submit handler throws, which would keep
  // Submit disabled after a failed save, and it drops the handler's result. So the handler settles
  // inside handleSubmit, and its outcome is rethrown or returned here.
  const submit = useCallback(async (): Promise<SubmitBlockers | null> => {
    let outcome = undefined as { heldBackBy: SubmitBlockers | null } | { error: unknown } | undefined;
    await handleSubmit(async (data) => {
      try {
        outcome = { heldBackBy: await onSubmit(data) };
      } catch (error) {
        outcome = { error };
      }
    })();
    if (outcome && 'error' in outcome) throw outcome.error;
    return outcome?.heldBackBy ?? null;
  }, [handleSubmit, onSubmit]);

  return {
    form,
    isSubmitting,
    onSubmit: submit,
  };
};
